
import * as React from "react"

const MOBILE_BREAKPOINT = 768
const XS_BREAKPOINT = 480
const TABLET_BREAKPOINT = 1024
const LG_BREAKPOINT = 1280

// SSR safe window width hook
const useWindowWidth = () => {
  const [width, setWidth] = React.useState<number | undefined>(undefined)
  
  React.useEffect(() => {
    // Function to update width
    const handleResize = () => {
      setWidth(window.innerWidth)
    }
    
    // Set initial width
    handleResize()
    
    // Add event listener with debounce for performance
    let timeoutId: number | undefined;
    const debouncedResize = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(handleResize, 100) as unknown as number;
    };
    
    window.addEventListener('resize', debouncedResize)
    
    // Clean up
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedResize)
    }
  }, [])
  
  return width
}

// Utility function to create responsiveness hooks
const createBreakpointHook = (
  breakpointCheck: (width: number) => boolean,
) => {
  return function useBreakpointHook() {
    const width = useWindowWidth()
    
    // Use a ref to track the last valid value for SSR
    const lastValidValue = React.useRef<boolean>(false);
    
    // If width is undefined (for SSR), return last known value
    if (width === undefined) return lastValidValue.current;
    
    // Update the last valid value
    const result = breakpointCheck(width);
    lastValidValue.current = result;
    
    return result;
  }
}

export const useIsMobile = createBreakpointHook(
  (width) => width < MOBILE_BREAKPOINT
)

export const useIsExtraSmall = createBreakpointHook(
  (width) => width < XS_BREAKPOINT
)

export const useIsTablet = createBreakpointHook(
  (width) => width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT
)

export const useIsDesktop = createBreakpointHook(
  (width) => width >= TABLET_BREAKPOINT
)

export const useIsLargeDesktop = createBreakpointHook(
  (width) => width >= LG_BREAKPOINT
)

export function useBreakpoint() {
  const width = useWindowWidth()
  const [breakpoint, setBreakpoint] = React.useState<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('md')

  React.useEffect(() => {
    // Don't update during SSR
    if (width === undefined) return
    
    // Update breakpoint based on width
    if (width < XS_BREAKPOINT) {
      setBreakpoint('xs')
    } else if (width < MOBILE_BREAKPOINT) {
      setBreakpoint('sm')
    } else if (width < TABLET_BREAKPOINT) {
      setBreakpoint('md')
    } else if (width < LG_BREAKPOINT) {
      setBreakpoint('lg')
    } else {
      setBreakpoint('xl')
    }
  }, [width])

  return breakpoint
}
